/**
 * @fileoverview Winbit32 Wallet Modal
 * Mnemonic import/generation flow for "Zcash by Winbit32".
 */

import React, { useMemo, useState } from 'react';
import { X, KeyRound, RefreshCw, AlertTriangle, Clock, Lock } from 'lucide-react';
import { validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import winbit32Wallet from '../../services/winbit32Wallet';
import type { Winbit32StorageOptions } from '../../services/zcashWallet';

interface Winbit32WalletModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConnect: (mnemonic: string, options: Winbit32StorageOptions) => Promise<void>;
}

const normaliseMnemonic = (value: string): string =>
	(value || '').trim().replace(/\s+/g, ' ');

// Minimum password length when one is supplied. Kept modest because the seed
// is the real secret; the password just gates at-rest access to the encrypted
// copy.
const MIN_PASSWORD_LENGTH = 8;

const Winbit32WalletModal: React.FC<Winbit32WalletModalProps> = ({
	isOpen,
	onClose,
	onConnect,
}) => {
	const [mnemonic, setMnemonic] = useState('');
	const [storageMode, setStorageMode] = useState<'session' | 'persistent'>('session');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isConnecting, setIsConnecting] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const wordCount = useMemo(() => {
		const normalised = normaliseMnemonic(mnemonic);
		return normalised ? normalised.split(' ').length : 0;
	}, [mnemonic]);

	const resetState = () => {
		setMnemonic('');
		setStorageMode('session');
		setPassword('');
		setConfirmPassword('');
		setIsConnecting(false);
		setIsGenerating(false);
		setError(null);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleGenerateMnemonic = async () => {
		setIsGenerating(true);
		setError(null);
		try {
			const phrase = await winbit32Wallet.generateMnemonic();
			setMnemonic(phrase);
			// A freshly generated seed's birthday is "now", so stamp a recent hint
			// in the background. The first balance scan then skips the slow cold
			// auto-detect and starts near the tip. Best-effort: failures are
			// non-fatal (detection still runs on the first scan).
			void winbit32Wallet.rememberFreshWalletBirthday(phrase);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to generate mnemonic';
			setError(message);
		} finally {
			setIsGenerating(false);
		}
	};

	const handleConnect = async () => {
		const normalised = normaliseMnemonic(mnemonic);
		if (!normalised) {
			setError('Mnemonic is required');
			return;
		}
		if (!validateMnemonic(normalised, wordlist)) {
			setError('Invalid mnemonic. Please enter a valid 12/24-word phrase.');
			return;
		}

		const wantsPassword = storageMode === 'persistent' || password.length > 0;
		if (storageMode === 'persistent' && !password) {
			setError('A password is required to remember your wallet on this device.');
			return;
		}
		if (wantsPassword) {
			if (password.length < MIN_PASSWORD_LENGTH) {
				setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
				return;
			}
			if (password !== confirmPassword) {
				setError('Passwords do not match.');
				return;
			}
		}

		setError(null);
		setIsConnecting(true);
		try {
			await onConnect(normalised, {
				mode: storageMode,
				password: wantsPassword ? password : undefined,
			});
			handleClose();
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to connect Winbit32 wallet';
			setError(message);
		} finally {
			setIsConnecting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

			<div className="flex items-center justify-center min-h-screen p-4">
				<div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full">
					<div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-xl">
						<div className="flex items-center justify-between">
							<h2 className="text-xl font-bold text-white">Zcash by Winbit32</h2>
							<button
								onClick={handleClose}
								className="text-white hover:text-gray-200 transition-colors"
								aria-label="Close modal"
							>
								<X className="w-6 h-6" />
							</button>
						</div>
						<p className="text-purple-100 text-sm mt-2">
							Import an existing mnemonic or generate a new wallet.
						</p>
					</div>

					<form
						className="p-6"
						noValidate
						onSubmit={(e) => {
							e.preventDefault()
							if (!isConnecting && mnemonic.trim()) void handleConnect()
						}}
					>
						{error && (
							<div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
								<p className="text-sm text-red-800">{error}</p>
							</div>
						)}

						<div className="mb-4 flex items-center justify-between">
							<label className="text-sm font-medium text-gray-700">
								Mnemonic Phrase (12/24 words)
							</label>
							<button
								type="button"
								onClick={handleGenerateMnemonic}
								disabled={isGenerating || isConnecting}
								className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isGenerating ? (
									<>
										<RefreshCw className="w-3 h-3 mr-1 animate-spin" />
										Generating...
									</>
								) : (
									<>
										<RefreshCw className="w-3 h-3 mr-1" />
										Generate New
									</>
								)}
							</button>
						</div>

						<textarea
							value={mnemonic}
							onChange={(e) => setMnemonic(e.target.value)}
							placeholder="Enter your mnemonic phrase..."
							rows={4}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
						/>
						<div className="mt-2 flex items-center justify-between text-xs text-gray-500">
							<span>{wordCount} words</span>
							<span>Stays in this browser — never sent to any server</span>
						</div>

						{/* Storage mode: session-only by default; persistent is opt-in
						    and always password-encrypted. */}
						<fieldset className="mt-5">
							<legend className="text-sm font-medium text-gray-700 mb-2">
								How should this device keep your seed?
							</legend>
							<div className="space-y-2">
								<label
									className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
										storageMode === 'session'
											? 'border-purple-400 bg-purple-50'
											: 'border-gray-200 hover:border-gray-300'
									}`}
								>
									<input
										type="radio"
										name="winbit32-storage-mode"
										value="session"
										checked={storageMode === 'session'}
										onChange={() => setStorageMode('session')}
										className="mt-1"
									/>
									<span className="flex-1">
										<span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
											<Clock className="w-4 h-4 text-purple-600" /> This session only
											<span className="text-xs font-normal text-purple-700">(recommended)</span>
										</span>
										<span className="block text-xs text-gray-600 mt-0.5">
											Held in this tab's session storage: it <strong>survives page reloads</strong>
											{' '}but is cleared when you close the tab. Never written to durable storage
											(browsers that reopen tabs after a restart may keep it for that restored
											session). Add a password below to encrypt it during the session too (optional).
										</span>
									</span>
								</label>

								<label
									className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
										storageMode === 'persistent'
											? 'border-purple-400 bg-purple-50'
											: 'border-gray-200 hover:border-gray-300'
									}`}
								>
									<input
										type="radio"
										name="winbit32-storage-mode"
										value="persistent"
										checked={storageMode === 'persistent'}
										onChange={() => setStorageMode('persistent')}
										className="mt-1"
									/>
									<span className="flex-1">
										<span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
											<Lock className="w-4 h-4 text-purple-600" /> Remember on this device
										</span>
										<span className="block text-xs text-gray-600 mt-0.5">
											Survives across visits, stored <strong>encrypted</strong> with the password below.
											You'll enter the password to unlock it next time. Requires a password.
										</span>
									</span>
								</label>
							</div>

							<div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">
										{storageMode === 'persistent' ? 'Password (required)' : 'Password (optional)'}
									</label>
									<input
										type="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										autoComplete="new-password"
										placeholder={storageMode === 'persistent' ? 'Encrypts your saved seed' : 'Leave blank to skip'}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
									/>
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">
										Confirm password
									</label>
									<input
										type="password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										autoComplete="new-password"
										disabled={storageMode === 'session' && password.length === 0}
										placeholder="Re-enter password"
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
									/>
								</div>
							</div>
							{storageMode === 'persistent' && (
								<p className="mt-2 text-xs text-amber-700">
									There is no password recovery. If you forget it, re-import your seed phrase to regain access.
								</p>
							)}
						</fieldset>

						<div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
							<div className="flex items-start space-x-3">
								<AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
								<div>
									<h4 className="text-sm font-medium text-yellow-900">Security warning</h4>
									<p className="text-sm text-yellow-700 mt-1">
										Never share your mnemonic phrase. Anyone with this phrase can access your funds.
									</p>
								</div>
							</div>
						</div>

						<div className="mt-6 flex space-x-3">
							<button
								type="button"
								onClick={handleClose}
								className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleConnect}
								disabled={isConnecting || !mnemonic.trim()}
								className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
							>
								{isConnecting ? (
									<>
										<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
										Connecting...
									</>
								) : (
									<>
										<KeyRound className="w-4 h-4 mr-2" />
										Connect Wallet
									</>
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default Winbit32WalletModal;

